import * as TabsPrimitive from '@radix-ui/react-tabs'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * `segmented` é o botão em pílula (padrão do app). `underline` é a aba do MUI:
 * sem caixa nem borda, só a barra de 2px que corre embaixo do rótulo ativo.
 */
const tabsListVariants = cva(
    [
        'flex max-w-full items-center justify-start',
        'overflow-x-auto overflow-y-hidden scrollbar-thin',
        'select-none',
    ],
    {
        variants: {
            variant: {
                segmented: [
                    'h-10 min-h-10 rounded-lg p-1 gap-1',
                    'border dark:border-0 dark:bg-card',
                ],
                underline: [
                    'h-12 min-h-12 gap-1 rounded-none p-0',
                    'border-0 bg-transparent',
                ],
            },
        },
        defaultVariants: { variant: 'segmented' },
    },
)

const tabsTriggerVariants = cva(
    [
        'flex items-center justify-center gap-1.5 whitespace-nowrap',
        'min-w-fit transition-colors duration-150',
        'data-[state=inactive]:cursor-pointer',
        'focus-visible:ring-[3px] focus-visible:outline-1',
        'disabled:pointer-events-none disabled:opacity-50 disabled:border-none',
        'disabled:cursor-not-allowed group-disabled:cursor-not-allowed',
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    ],
    {
        variants: {
            variant: {
                segmented: [
                    'flex h-[calc(100%-1px)] flex-1',
                    'gap-1.5 rounded-md border border-transparent',
                    'px-2 py-1 text-sm font-medium',
                    'bg-card hover:bg-primary/20',
                    'data-[state=active]:bg-primary data-[state=active]:shadow-sm',
                    'text-primary-foreground dark:data-[state=inactive]:text-foreground',
                    'data-[state=inactive]:text-foreground',
                ],
                underline: [
                    'relative h-full flex-none rounded-none border-0 shadow-none',
                    'bg-transparent px-4 text-base font-medium',
                    'text-muted-foreground hover:text-foreground',
                    'data-[state=active]:text-foreground',
                    "after:pointer-events-none after:absolute after:content-['']",
                    'after:inset-x-0 after:bottom-0 after:h-[3px] after:rounded-full',
                    'after:bg-transparent after:transition-colors after:duration-200',
                    'data-[state=active]:after:bg-primary',
                    'text-sm tracking-tight uppercase font-medium',
                ],
            },
        },
        defaultVariants: { variant: 'segmented' },
    },
)

function Tabs({
    className,
    ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
    return (
        <TabsPrimitive.Root
            data-slot="tabs"
            className={cn(
                'size-full flex flex-col gap-2',
                'shadow-sm dark:shadow-none rounded-md',
                'dark:bg-card/50',
                className,
            )}
            {...props}
        />
    )
}

function TabsList({
    className,
    variant,
    ...props
}: React.ComponentProps<typeof TabsPrimitive.List> &
    VariantProps<typeof tabsListVariants>) {
    return (
        <TabsPrimitive.List
            data-slot="tabs-list"
            className={cn(tabsListVariants({ variant }), className)}
            {...props}
        />
    )
}

function TabsTrigger({
    className,
    variant,
    ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger> &
    VariantProps<typeof tabsTriggerVariants>) {
    return (
        <TabsPrimitive.Trigger
            data-slot="tabs-trigger"
            className={cn(tabsTriggerVariants({ variant }), className)}
            {...props}
        />
    )
}

function TabsContent({
    className,
    ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
    return (
        <TabsPrimitive.Content
            data-slot="tabs-content"
            className={cn('flex-1 outline-none p-4 pt-0', className)}
            {...props}
        />
    )
}

export { Tabs, TabsContent, TabsList, TabsTrigger }
